import type { Rating } from "../types/rating";
import type { AchievementExperienceItem, ProgressExperienceItem } from "../types/experience";
import type { WeeklyDungeon, WeeklyDungeonState, WeeklyQuestCard, WeeklyQuestState } from "../types/games";
import type { EntityId } from "../types/media";
import { isUnknownArray, dateValueToMillis } from "../types/data";
import { WEEKLY_QUEST_RARITIES } from "./weeklyQuestCards";
import { getWeeklyDungeonReward } from "./weeklyDungeons";
import {
  deduplicateRatingsByMediaId,
  isGameProgressEligible
} from "./gameProgressEligibility";

export type { ProgressExperienceItem } from "../types/experience";

const asArray = <T>(value: T | T[] | null | undefined): T[] => isUnknownArray(value) ? value : [];
const CHALLENGE_DAY_MS = 24 * 60 * 60 * 1000;

const getChallengeRatingTime = (rating: Rating = {}) => {
  const value = rating.firstTrackedAt || rating.firstRatedAt || rating.ratedAt;
  if (!value) return 0;
  return dateValueToMillis(value);
};

const getDungeonRarityFromId = (selectedDungeonId: unknown) => {
  const match = String(selectedDungeonId || "").match(/_(common|epic|legendary)_/);
  return match?.[1] || "common";
};

const cardDifficulty = (rarity: string) =>
  rarity === "legendary" ? "impossible" : rarity === "epic" ? "hard" : "medium";

const dungeonDifficulty = (rarity: string) =>
  rarity === "legendary" ? "impossible" : rarity === "epic" ? "hard" : "medium";

export const buildCompletedChallengeExperienceItems = (
  challenges: ProgressExperienceItem[] = [],
  ratings: Rating[] = [],
  fallbackCompletedChallenges: ProgressExperienceItem[] = []
) => {
  const sourceChallenges = asArray(challenges).length
    ? challenges
    : fallbackCompletedChallenges;
  const sourceRatings = asArray(ratings);
  const completedById = new Map<string, ProgressExperienceItem>();

  asArray(sourceChallenges).forEach((challenge) => {
    if (!challenge?.id) return;
    const startedAt = Number(challenge.startedAt);
    const target = Number(challenge.target);
    const durationDays = Number(challenge.durationDays);
    const canRecalculate =
      Number.isFinite(startedAt) &&
      Number.isFinite(target) &&
      target > 0 &&
      typeof challenge.getProgress === "function";

    if (!canRecalculate) {
      if (challenge.status === "completed") {
        completedById.set(String(challenge.id), challenge);
      }
      return;
    }

    const deadline = Number.isFinite(durationDays) && durationDays > 0
      ? startedAt + durationDays * CHALLENGE_DAY_MS
      : Number.POSITIVE_INFINITY;
    const progressStartGraceMs = Math.max(
      0,
      Number(challenge.progressStartGraceMs) || 0
    );
    const eligibleRatings = deduplicateRatingsByMediaId(
      sourceRatings.filter((rating: Rating) => {
        if (rating?.active === false || !isGameProgressEligible(rating)) return false;
        const ratedAt = getChallengeRatingTime(rating);
        return ratedAt >= startedAt - progressStartGraceMs && ratedAt < deadline;
      })
    );
    const progress = Math.min(target, Number(challenge.getProgress!(eligibleRatings)) || 0);

    if (progress >= target) {
      completedById.set(String(challenge.id), {
        ...challenge,
        status: "completed",
        progress,
        deadline,
        experience: Number(challenge.experience) || challenge.experience
      });
    }
  });

  return [...completedById.values()];
};

export const getWeeklyQuestExperienceItemId = (weekId: string, cardId: string) =>
  `weekly-card:${weekId}:${cardId}`;

export const getProgressExperienceComponentId = (item: ProgressExperienceItem = {}) => {
  const sourceType = String(item.sourceType || "challenge");
  const sourceId = String(item.id || item.sourceId || item.title || "");
  if (!sourceId) return "";
  const instanceKey = sourceType === "challenge"
    ? String(Number(item.startedAt) || "legacy")
    : "";
  return [sourceType, sourceId, instanceKey].filter(Boolean).join(":");
};

export const getAchievementExperienceComponentId = (achievement: AchievementExperienceItem = {}) => {
  const achievementKey = achievement.id != null
    ? `id:${achievement.id}`
    : `title:${achievement.title || ""}`;
  return ["achievement", achievementKey, String(achievement.title || "")].join(":");
};

export const getWeeklyDungeonExperienceItemId = (weekId: string, dungeonId: string) =>
  `weekly-dungeon:${weekId}:${dungeonId}`;

type QuestRewardState = Pick<Partial<WeeklyQuestState>, "weekId" | "rewardedQuestIds"> & {
  cards?: Pick<Partial<WeeklyQuestCard>, "id" | "rarity" | "reward">[];
};
export const buildWeeklyQuestExperienceItems = (weeklyStates: QuestRewardState[] = []) => {
  const itemsById = new Map<string, ProgressExperienceItem>();

  asArray(weeklyStates).forEach((state) => {
    const weekId = state?.weekId || "";
    if (!weekId) return;

    const cardsById = new Map(
      asArray(state.cards)
        .filter((card) => card?.id)
        .map((card) => [String(card.id), card])
    );
    const rewardedIds = [...new Set(asArray(state.rewardedQuestIds).map(String))];

    rewardedIds.forEach((cardId) => {
      const card = cardsById.get(cardId);
      if (!card) return;

      const rarity = card.rarity || "common";
      const rarityMeta = WEEKLY_QUEST_RARITIES[rarity] || WEEKLY_QUEST_RARITIES.common;
      const experience = Number(card.reward ?? rarityMeta.reward) || 0;
      if (experience <= 0) return;

      const id = getWeeklyQuestExperienceItemId(weekId, cardId);
      itemsById.set(id, {
        id,
        sourceId: cardId,
        sourceType: "weekly_card",
        title: `Карта: ${rarityMeta.label}`,
        status: "completed",
        experience,
        difficulty: cardDifficulty(rarity),
        rarity,
        weekId
      });
    });
  });

  return [...itemsById.values()]
    .sort((left, right) => `${left.weekId}:${left.sourceId}`.localeCompare(`${right.weekId}:${right.sourceId}`));
};

type DungeonRewardState = WeeklyDungeonState & { rarity?: string; reward?: number };
export const buildWeeklyDungeonExperienceItems = (dungeonStates: DungeonRewardState[] = [], dungeonById: Map<string, WeeklyDungeon> = new Map<string, WeeklyDungeon>()) => {
  const itemsById = new Map<string, ProgressExperienceItem>();

  asArray(dungeonStates).forEach((state) => {
    const weekId = state?.weekId || "";
    const selectedDungeonId = state?.selectedDungeonId || "";
    if (!weekId || !selectedDungeonId || state?.status !== "completed") return;
    if (state.failureReason === "rating_deleted" || state.revokedAt) return;

    const dungeon = dungeonById.get(selectedDungeonId) || null;
    const rarity = dungeon?.rarity || state.rarity || getDungeonRarityFromId(selectedDungeonId);
    const experience = getWeeklyDungeonReward(rarity, dungeon?.reward || state.reward);
    if (experience <= 0) return;

    const id = getWeeklyDungeonExperienceItemId(weekId, selectedDungeonId);
    itemsById.set(id, {
      id,
      sourceId: selectedDungeonId,
      sourceType: "weekly_dungeon",
      title: dungeon?.title ? `Экспедиция: ${dungeon.title}` : "Экспедиция",
      status: "completed",
      experience,
      rarity,
      difficulty: dungeonDifficulty(rarity),
      weekId
    });
  });

  return [...itemsById.values()]
    .sort((left, right) => `${left.weekId}:${left.sourceId}`.localeCompare(`${right.weekId}:${right.sourceId}`));
};

export const filterRevokedExperienceItems = <T extends ProgressExperienceItem>(items: T[] = [], revokedItemIds: EntityId[] = []): T[] => {
  const revoked = new Set(asArray(revokedItemIds).map(String));
  if (!revoked.size) return asArray(items);
  return asArray(items).filter((item) => !revoked.has(String(item?.id || "")));
};
